import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send, MessageCircle, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import api from '../lib/api';
import { getWhatsAppLink, WHATSAPP_NUMBER } from '../lib/utils';
import SuccessModal from '../components/SuccessModal';

const contactInfo = [
  { icon: MapPin, title: 'Bureau Principal', details: ['Kinshasa, RDC'], color: 'text-[#D4AF37]' },
  { icon: MapPin, title: 'Représentation', details: ['Dubaï, EAU'], color: 'text-sky-400' },
  { icon: Phone, title: 'Téléphone', details: ['+243 975 608 422'], href: 'tel:+243975608422', color: 'text-[#D4AF37]' },
  { icon: Mail, title: 'Email', details: ['contact@globalwings-rdc.com'], href: 'mailto:contact@globalwings-rdc.com', color: 'text-[#D4AF37]' },
  { icon: Clock, title: 'Horaires', details: ['Lun - Sam: 8h00 - 18h00'], color: 'text-[#D4AF37]' }
];

export const ContactPage = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/contact', formData);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setShowSuccess(true);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#020617]">
      {/* Hero */}
      <section className="pt-6 pb-4 relative" data-testid="contact-hero">
        <div className="absolute inset-0 gradient-radial opacity-30" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#94A3B8] hover:text-[#D4AF37] transition-colors mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </button>
          
          <motion.div className="text-center" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12 bg-[#D4AF37]" />
              <span className="text-[#D4AF37] text-sm font-medium tracking-[0.2em] uppercase">Contact</span>
              <div className="h-px w-12 bg-[#D4AF37]" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Contactez-<span className="text-[#D4AF37] italic">Nous</span>
            </h1>
            <p className="text-lg text-[#94A3B8] max-w-2xl mx-auto">
              Notre équipe est disponible pour répondre à toutes vos questions
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-8 relative" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 gap-12">
            {/* Info */}
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl font-bold text-white mb-6">Nos Coordonnées</h2>
              <p className="text-[#94A3B8] mb-8">Depuis 2011, Global Wings accompagne ses clients dans leurs projets de voyage.</p>
              
              <div className="space-y-4 mb-8">
                {contactInfo.map((item, index) => (
                  <div key={index} className="card-luxury p-4 flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0">
                      <item.icon className={`w-6 h-6 ${item.color}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">{item.title}</h3>
                      {item.details.map((detail, i) => (
                        item.href ? (
                          <a key={i} href={item.href} className="block text-[#94A3B8] hover:text-[#D4AF37] transition-colors">{detail}</a>
                        ) : (
                          <p key={i} className="text-[#94A3B8]">{detail}</p>
                        )
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <a href={getWhatsAppLink(WHATSAPP_NUMBER, 'Bonjour')} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-btn">
                <button className="btn-gold px-8 py-4 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  Contacter via WhatsApp
                </button>
              </a>
            </motion.div>

            {/* Form */}
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="card-luxury p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Envoyez un Message</h2>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                  <div>
                    <Label className="text-[#94A3B8]">Nom Complet *</Label>
                    <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37]" placeholder="Votre nom" data-testid="input-name" />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8]">Email *</Label>
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37]" placeholder="votre@email.com" data-testid="input-email" />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8]">Téléphone</Label>
                    <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37]" placeholder="+243..." data-testid="input-phone" />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8]">Sujet *</Label>
                    <Input value={formData.subject} onChange={(e) => setFormData({...formData, subject: e.target.value})} required
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37]" placeholder="Sujet du message" data-testid="input-subject" />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8]">Message *</Label>
                    <Textarea value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} required rows={5}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] resize-none" placeholder="Votre message..." data-testid="input-message" />
                  </div>
                  <button type="submit" className="w-full btn-gold py-4 flex items-center justify-center gap-2" disabled={submitting} data-testid="submit-btn">
                    {submitting ? 'Envoi...' : (<><Send className="w-5 h-5" />Envoyer</>)}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <SuccessModal 
        isOpen={showSuccess} 
        onClose={() => setShowSuccess(false)}
        title="Message envoyé !"
        message="Votre message a été transmis avec succès. Notre équipe vous répondra dans les plus brefs délais."
      />
    </div>
  );
};

export default ContactPage;
